import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { prepareEmbeddableWidgetContainer } from './embeddableWidgetPortalUtils';
import { WidgetProviders } from './WidgetProviders';
import {
  WidgetHostMessage,
  type WidgetHostMessageProps,
} from './WidgetHostMessage';

const domRoots = new WeakMap<HTMLElement, Root>();

/**
 * Renders a status message into a widget mount node (replacing any prior root).
 */
export function renderWidgetHostMessage(
  dom: HTMLElement,
  props: WidgetHostMessageProps,
): Root {
  const existing = domRoots.get(dom);
  existing?.unmount();

  prepareEmbeddableWidgetContainer(dom);

  const root = createRoot(dom);
  root.render(
    <WidgetProviders portalContainer={dom}>
      <WidgetHostMessage {...props} />
    </WidgetProviders>,
  );
  domRoots.set(dom, root);
  return root;
}

/**
 * Mounts a React tree into a widget host node (widget content or status).
 */
export function mountWidgetHostTree(
  dom: HTMLElement,
  element: React.ReactElement,
): Root {
  const existing = domRoots.get(dom);
  existing?.unmount();

  const root = createRoot(dom);
  root.render(element);
  domRoots.set(dom, root);
  return root;
}

export function unmountWidgetHost(dom: HTMLElement): void {
  const root = domRoots.get(dom);
  if (root) {
    root.unmount();
    domRoots.delete(dom);
  }
}
